import { Label } from '@common/components/Typography';
import type { Placement } from '@floating-ui/react';
import moment from 'moment-timezone';
import { FC, forwardRef } from 'react';
import { ReactDatePickerProps } from 'react-datepicker';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import StyledDatePicker from './Calendar.styles';
import { ChevronInput } from './ChevronInput';
import { InputWrapper } from './InputWrapper';

export const NotEditableInput = forwardRef<HTMLInputElement>((props, ref) => (
  <ChevronInput>
    <input {...props} readOnly ref={ref} />
  </ChevronInput>
));

type CalendarProps = Omit<ReactDatePickerProps, 'minDate' | 'onChange'> & {
  input: {
    value: moment.Moment;
    onChange: (date: moment.Moment) => void;
  };
  minDate: moment.Moment | Date;
  excludeDates?: Date[];
  startDate?: Date;
  endDate?: Date;
  dateFormat?: string;
  label?: string;
  popperPlacement?: Placement;
  id?: string;
};

const CalendarComponent: FC<CalendarProps & WrappedComponentProps> = ({
  input: { value, onChange },
  minDate,
  excludeDates,
  startDate,
  endDate,
  dateFormat,
  label,
  popperPlacement,
  intl,
  id,
}) => {
  function handleOnChange(date: Date | null) {
    if (!date) return;
    const parsedOnChangeValue = moment.tz(
      moment(date).format('YYYY-MM-DD'),
      intl.timeZone as string
    );
    onChange(parsedOnChangeValue);
  }

  const parsedValue = value ? value.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
  const formattedMinDate = minDate ? moment(minDate).format('YYYY-MM-DD') : undefined;
  const inputFormattedValue = moment(parsedValue).format(dateFormat);

  return (
    <InputWrapper>
      <Label htmlFor={id}>{label}</Label>
      <StyledDatePicker
        id={id}
        selected={new Date(parsedValue)}
        onChange={handleOnChange}
        minDate={formattedMinDate ? new Date(formattedMinDate) : undefined}
        excludeDates={excludeDates}
        startDate={startDate ? new Date(startDate) : undefined}
        endDate={endDate}
        value={inputFormattedValue}
        popperPlacement={popperPlacement}
        customInput={<NotEditableInput />}
      />
    </InputWrapper>
  );
};

export const Calendar = injectIntl(CalendarComponent);
